import { useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import React, { useState, useCallback } from "react";
import { queryClient } from "src";
import BinApiService from "src/api/BinApiService";
import { binDBKeys } from "src/pages/binOverview/BinCommander";
import { RangeValue } from "./ChartContainer";
import { FanControlMoisturePreferenceProps, tooltipItemRound, useFanHeaterChangeHistory } from "./shared";
import { BaseChartProps, htmlLegendPlugin } from "./TemperatureCableChart";
import _uniqueId from 'lodash/uniqueId';
import { Line } from "react-chartjs-2";
import { ChartDatePicker } from "./Co2Chart";
import { Skeleton } from "antd";
import { ambientFanDatasetColor, plenumFanDatasetColor } from "./AmbientPlenumChart";
import { LineAnnotationOptions } from "chartjs-plugin-annotation";
import FanOffReason from "src/consts/FanOffReason";
import HeaterOffReason from "src/consts/HeaterOffReason";
import ModeChangeDataPointDTO from "src/models/ModeChangeDataPointDTO";
import { queryOptions } from "src/queries/v4QueryOptions/queryOptions";
import { BaseChartQueryOptions, useModeChangeHistory } from "./modehistoryAndFanChangeHistory";

export const useLayerMcHistoryQueryOptions = (options: BaseChartQueryOptions) => {
  const queryClient = useQueryClient();
  const value = options.range;

  return queryOptions(
    {
      queryKey: [...binDBKeys.bin(options.binId), "charts", "LayerSummary", { startDate: value?.[0], endDate: value?.[1] }],
      queryFn: async (q) => {
        const now = value?.[1];
        const priorMonth = value?.[0];
  
        const result = await BinApiService.getLayerSummaryHistory(options.binId, priorMonth?.toISOString()!, now?.toISOString()!, null, q.signal);
        queryClient.removeQueries({ queryKey: q.queryKey.slice(0, -1), type: "inactive" });
        return result;
      },
      refetchInterval: Infinity, retry: false, keepPreviousData: true,
      enabled: (options.binId ?? 0) > 0 && value?.[0] != null && value?.[1] != null,
    }
  )
}

export const LayerMCHistoryChart = (props: BaseChartProps & {yTitle?: string} & FanControlMoisturePreferenceProps) => {
  const currentDate = dayjs();
    const priorDate = currentDate.subtract(14, 'days');
    
    const [legendId] = useState(_uniqueId('legend-'));
    const isControlled = typeof props.value != 'undefined';
    const [internalValue, setIntervalValue] = useState<RangeValue>([priorDate, currentDate]);
  
    // Internally, we need to deal with some value. Depending on whether
      // the component is controlled or not, that value comes from its
      // props or from its internal state.
  
      const value = isControlled ? props.value : internalValue;
  
    const onDateChange = useCallback((values: RangeValue, formatter: any) => {
      if (props.onChange) {
        props.onChange(values);
      }
      else {
        setIntervalValue(values);
      }
    }, [props.onChange]);

    const modeHistory = useModeChangeHistory(props.binId, value?.[0], value?.[1]);
    const fanHeaterChangeHistory = useFanHeaterChangeHistory(props.binId, value?.[0], value?.[1]);
    
    const layerMcHistoryQueryOptions = useLayerMcHistoryQueryOptions({
      binId: props.binId,
      range: value,
    })


    const chartQuery = useQuery({...layerMcHistoryQueryOptions});

    const formatYTitle = useCallback(() => {
      if (props.useRHForFanControl === false) {
        return "MC (%)";
      } else {
        return "RH %";
      }
    }, [props.useRHForFanControl]);

    const getYSuggestedMinMax = () => {
      if (props.useRHForFanControl === false) {
        return {min: undefined, max: undefined}
      } else {
        return {min: 0, max: 100};
      }
    }
  
  
    return (<div>
      {!isControlled && <ChartDatePicker startDate={value?.[0]!} endDate={value?.[1]!} onDateRangeChange={onDateChange} />}
      <Skeleton loading={chartQuery.data == null}>
        {chartQuery.data && <section style={{ width: "100%", height: props.chartHeight ?? "480px", display: "flex", justifyContent: "center" }}>
          <Line options={{
            responsive: true,
            animation: false,
            spanGaps: false,
            resizeDelay: 250,
            maintainAspectRatio: false,
            datasets: {
              line: {
                pointRadius: 0 // disable for all `'line'` datasets
              }
            },
            scales: {
              x: {
                type: "time",
                time: {
                  minUnit: 'day',
                  displayFormats: {
                    //day: 'l'
                  }
                },
              },
              y: {
                min: props.min ?? getYSuggestedMinMax().min,
                max: props.max ?? getYSuggestedMinMax().max,
                title: {
                  font: {
                    size: 16,
                  },
                  display: true,
                  text: props.yTitle ?? formatYTitle(),
                }
              }
            },
            interaction: {
              includeInvisible: true,            
            },
            plugins: {
              annotation: {
                annotations: [
                  ...modeHistory.annotations,
                  ...fanHeaterChangeHistory.annotations,
                ],
              },
              legend: {
                position: 'top' as const,
                display: true,
              },
              title: {
                display: true,
                text: 'Moisture Cables',
              },
  
              tooltip: {
                mode: 'index',
                
                intersect: false,
                callbacks: {
                  label: tooltipItemRound,
                },
              },
  
            },
          }}
            data={

                { datasets: [
                     ...[...chartQuery.data ?? []]?.reverse()?.map(dataset => {

                return {
                    data: dataset.data,
                    label: dataset.label!,
                    parsing: {
                      yAxisKey: props.useRHForFanControl === false ? 'mc' : 'relativeHumidity',
                    }
                };
            }) ?? [],

            ] }} />
        </section>
        }
        <section id={`legend-layerSummary-container${legendId}`}></section>
      </Skeleton>
    </div>);
  }
  